import { Box, Typography } from '@mui/material'
import type { ComponentType, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import NiSquircle from '@/ui/icons/nexture/ni-squircle'
import type { NextureIconsProps } from '@/ui/icons/types'

export interface EmptyProps {
  title: string
  /** Uma frase. O que fazer, não o que aconteceu. */
  hint?: string | undefined
  /** O mesmo que `hint`, para quem precisa de texto com link ou negrito. */
  description?: ReactNode | undefined
  action?: ReactNode | undefined
  /** Ícone no anel tracejado. Sem ele, o quadrado neutro. */
  icon?: ComponentType<NextureIconsProps> | undefined
  className?: string | undefined
  /** Para seção dentro de uma tela que tem outras: menos altura, anel menor. */
  compact?: boolean | undefined
}

/**
 * Estado vazio no desenho da grade vazia do teles: ícone num anel tracejado,
 * título e a dica em cinza que passa em 4,5:1. Faz parte da tela.
 */
export function Empty({
  title,
  hint,
  description,
  action,
  icon: Icon = NiSquircle,
  className,
  compact,
}: EmptyProps) {
  return (
    <Box
      data-slot="empty"
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        compact ? 'py-4' : 'py-12',
        className,
      )}
    >
      <Box
        aria-hidden
        className={cn(
          'text-text-disabled relative flex items-center justify-center',
          compact ? 'mb-3 h-12 w-12' : 'mb-4 h-16 w-16',
        )}
        sx={{
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            outline: '1.5px dashed',
            outlineColor: 'var(--mui-palette-divider)',
          },
        }}
      >
        <Icon size={compact ? 20 : 26} />
      </Box>
      <Box className="max-w-sm">
        <Typography variant="subtitle1" component="p" className="text-text-primary">
          {title}
        </Typography>
        {(description ?? hint) !== undefined && (
          <Typography variant="body2" className="text-text-primary-light mt-1">
            {description ?? hint}
          </Typography>
        )}
        {action !== undefined && <Box className="mt-4 flex justify-center">{action}</Box>}
      </Box>
    </Box>
  )
}
